import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Wallet,
  Calculator,
  FileText,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  Gift,
  Plus,
  RefreshCw,
  Download,
  Eye,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatRupiah = (num) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num);
};

// Get current month options
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })
    });
  }
  return months;
};

const PayrollPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("payroll");
  const [loading, setLoading] = useState(false);
  const [payrollList, setPayrollList] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [thrList, setThrList] = useState([]);
  
  // Filters
  const [selectedPeriode, setSelectedPeriode] = useState(getMonthOptions()[0]?.value || "");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialogs
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showSalaryDialog, setShowSalaryDialog] = useState(false);
  const [showPayslipDialog, setShowPayslipDialog] = useState(false);
  const [showThrDialog, setShowThrDialog] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  
  // Form states
  const [generateMonth, setGenerateMonth] = useState(new Date().getMonth() + 1);
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear());
  const [salaryForm, setSalaryForm] = useState({
    employee_id: "",
    gaji_pokok: "",
    tunjangan_tetap: "",
    tunjangan_makan: "",
    tunjangan_kesehatan: "",
    tunjangan_lainnya: "",
  });
  const [thrForm, setThrForm] = useState({
    tahun: new Date().getFullYear(),
    tipe: "thr",
    jumlah_bulan: 1,
  });

  // Fetch data
  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (activeTab === "payroll" && selectedPeriode) {
      fetchPayroll();
      fetchSummary();
    } else if (activeTab === "salary") {
      fetchSalaryStructures();
    } else if (activeTab === "thr") {
      fetchThrList();
    }
    // eslint-disable-next-line
  }, [activeTab, selectedPeriode, selectedDepartment, selectedYear]);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      let url = `${API_URL}/api/payroll?periode=${selectedPeriode}`;
      if (selectedDepartment !== "all") {
        url += `&department_id=${selectedDepartment}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayrollList(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Gagal memuat data payroll");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/payroll/summary?periode=${selectedPeriode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSalaryStructures = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      let url = `${API_URL}/api/salary/structure`;
      if (selectedDepartment !== "all") {
        url += `?department_id=${selectedDepartment}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalaryStructures(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Gagal memuat struktur gaji");
    } finally {
      setLoading(false);
    }
  };

  const fetchThrList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/thr?tahun=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThrList(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Actions
  const handleGeneratePayroll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.post(`${API_URL}/api/payroll/generate`, {
        bulan: generateMonth,
        tahun: generateYear
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Berhasil generate ${res.data.length} payroll`);
      setShowGenerateDialog(false);
      setSelectedPeriode(`${generateYear}-${String(generateMonth).padStart(2, '0')}`);
      fetchPayroll();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal generate payroll");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSalary = async () => {
    if (!salaryForm.employee_id || !salaryForm.gaji_pokok) {
      toast.error("Karyawan dan gaji pokok wajib diisi");
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/salary/structure`, {
        employee_id: salaryForm.employee_id,
        gaji_pokok: parseFloat(salaryForm.gaji_pokok) || 0,
        tunjangan_tetap: parseFloat(salaryForm.tunjangan_tetap) || 0,
        tunjangan_makan: parseFloat(salaryForm.tunjangan_makan) || 0,
        tunjangan_kesehatan: parseFloat(salaryForm.tunjangan_kesehatan) || 0,
        tunjangan_lainnya: parseFloat(salaryForm.tunjangan_lainnya) || 0,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Struktur gaji berhasil disimpan");
      setShowSalaryDialog(false);
      setSalaryForm({ employee_id: "", gaji_pokok: "", tunjangan_tetap: "", tunjangan_makan: "", tunjangan_kesehatan: "", tunjangan_lainnya: "" });
      fetchSalaryStructures();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan struktur gaji");
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayroll = async (id) => {
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/payroll/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payroll disetujui");
      fetchPayroll();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal approve payroll");
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/payroll/${id}/pay`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payroll ditandai sudah dibayar");
      fetchPayroll();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal update status");
    }
  };

  const handleApproveAll = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.post(`${API_URL}/api/payroll/approve-all?periode=${selectedPeriode}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchPayroll();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal approve");
    }
  };

  const handleViewPayslip = async (item) => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/payslip/${item.employee_id}/${item.periode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPayslip(res.data);
      setShowPayslipDialog(true);
    } catch (err) {
      toast.error("Gagal memuat slip gaji");
    }
  };

  const handleCalculateThr = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.post(`${API_URL}/api/thr/calculate`, thrForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Berhasil menghitung ${res.data.length} ${thrForm.tipe.toUpperCase()}`);
      setShowThrDialog(false);
      fetchThrList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghitung THR");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "draft": return <Badge variant="secondary" data-testid="status-draft"><Clock className="w-3 h-3 mr-1" />Draft</Badge>;
      case "approved": return <Badge variant="default" className="bg-blue-500" data-testid="status-approved"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case "paid": return <Badge variant="default" className="bg-green-500" data-testid="status-paid"><DollarSign className="w-3 h-3 mr-1" />Paid</Badge>;
      case "calculated": return <Badge variant="secondary" data-testid="status-calculated"><Calculator className="w-3 h-3 mr-1" />Calculated</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Payroll Management</h1>
          <p className="text-slate-600">Kelola penggajian karyawan</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Wallet className="w-4 h-4 mr-2" />Payroll</TabsTrigger>
          <TabsTrigger value="salary" data-testid="tab-salary"><Calculator className="w-4 h-4 mr-2" />Struktur Gaji</TabsTrigger>
          <TabsTrigger value="thr" data-testid="tab-thr"><Gift className="w-4 h-4 mr-2" />THR & Bonus</TabsTrigger>
        </TabsList>

        {/* Payroll Tab */}
        <TabsContent value="payroll" className="space-y-4">
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Karyawan</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-blue-500 mr-2" />
                    <span className="text-2xl font-bold" data-testid="total-employees">{summary.total_karyawan}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Gaji Bruto</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-green-500 mr-2" />
                    <span className="text-xl font-bold" data-testid="total-bruto">{formatRupiah(summary.total_pendapatan)}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Potongan</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-red-500 mr-2" />
                    <span className="text-xl font-bold" data-testid="total-potongan">{formatRupiah(summary.total_potongan)}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Total Gaji Bersih</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center">
                    <Wallet className="w-5 h-5 text-purple-500 mr-2" />
                    <span className="text-xl font-bold" data-testid="total-netto">{formatRupiah(summary.total_gaji_bersih)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters & Actions */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-4 items-center">
                  <div className="w-48">
                    <Label>Periode</Label>
                    <Select value={selectedPeriode} onValueChange={setSelectedPeriode}>
                      <SelectTrigger data-testid="select-periode">
                        <SelectValue placeholder="Pilih periode" />
                      </SelectTrigger>
                      <SelectContent>
                        {getMonthOptions().map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label>Departemen</Label>
                    <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                      <SelectTrigger data-testid="select-department">
                        <SelectValue placeholder="Semua" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Departemen</SelectItem>
                        {departments.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { fetchPayroll(); fetchSummary(); }} data-testid="btn-refresh">
                    <RefreshCw className="w-4 h-4 mr-2" />Refresh
                  </Button>
                  <Button variant="outline" onClick={handleApproveAll} data-testid="btn-approve-all">
                    <CheckCircle className="w-4 h-4 mr-2" />Approve All
                  </Button>
                  <Button onClick={() => setShowGenerateDialog(true)} data-testid="btn-generate">
                    <Calculator className="w-4 h-4 mr-2" />Generate Payroll
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payroll Table */}
          <Card>
            <CardHeader>
              <CardTitle>Daftar Payroll</CardTitle>
              <CardDescription>Periode: {selectedPeriode}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : payrollList.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Wallet className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada data payroll untuk periode ini</p>
                  <Button className="mt-4" onClick={() => setShowGenerateDialog(true)} data-testid="btn-generate-empty">
                    Generate Payroll
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>NIK</TableHead>
                      <TableHead>Nama</TableHead>
                      <TableHead>Departemen</TableHead>
                      <TableHead className="text-right">Gaji Pokok</TableHead>
                      <TableHead className="text-right">Tunjangan</TableHead>
                      <TableHead className="text-right">Potongan</TableHead>
                      <TableHead className="text-right">Gaji Bersih</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollList.map((item) => (
                      <TableRow key={item.id} data-testid={`payroll-row-${item.id}`}>
                        <TableCell className="font-mono">{item.employee_nik}</TableCell>
                        <TableCell className="font-medium">{item.employee_nama}</TableCell>
                        <TableCell>{item.department_nama}</TableCell>
                        <TableCell className="text-right">{formatRupiah(item.gaji_pokok)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatRupiah(item.total_tunjangan + item.lembur_nominal)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatRupiah(item.total_potongan)}</TableCell>
                        <TableCell className="text-right font-bold">{formatRupiah(item.total_gaji_bersih)}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleViewPayslip(item)} data-testid={`btn-view-${item.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {item.status === "draft" && (
                              <Button variant="ghost" size="sm" onClick={() => handleApprovePayroll(item.id)} data-testid={`btn-approve-${item.id}`}>
                                <CheckCircle className="w-4 h-4 text-blue-500" />
                              </Button>
                            )}
                            {item.status === "approved" && (
                              <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(item.id)} data-testid={`btn-pay-${item.id}`}>
                                <DollarSign className="w-4 h-4 text-green-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Salary Structure Tab */}
        <TabsContent value="salary" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex justify-between items-center">
                <div className="w-48">
                  <Label>Filter Departemen</Label>
                  <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                    <SelectTrigger>
                      <SelectValue placeholder="Semua" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Departemen</SelectItem>
                      {departments.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setShowSalaryDialog(true)} data-testid="btn-add-salary">
                  <Plus className="w-4 h-4 mr-2" />Tambah Struktur Gaji
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Struktur Gaji Karyawan</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : salaryStructures.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calculator className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada struktur gaji yang diatur</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>NIK</TableHead>
                      <TableHead>Nama</TableHead>
                      <TableHead>Posisi</TableHead>
                      <TableHead>Departemen</TableHead>
                      <TableHead className="text-right">Gaji Pokok</TableHead>
                      <TableHead className="text-right">Tunjangan Tetap</TableHead>
                      <TableHead className="text-right">Tunjangan Makan</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salaryStructures.map((item) => (
                      <TableRow key={item.id} data-testid={`salary-row-${item.id}`}>
                        <TableCell className="font-mono">{item.employee_nik}</TableCell>
                        <TableCell className="font-medium">{item.employee_nama}</TableCell>
                        <TableCell>{item.position_nama}</TableCell>
                        <TableCell>{item.department_nama}</TableCell>
                        <TableCell className="text-right">{formatRupiah(item.gaji_pokok)}</TableCell>
                        <TableCell className="text-right">{formatRupiah(item.tunjangan_tetap)}</TableCell>
                        <TableCell className="text-right">{formatRupiah(item.tunjangan_makan)}</TableCell>
                        <TableCell className="text-right font-bold">{formatRupiah(item.gaji_pokok + item.total_tunjangan)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* THR & Bonus Tab */}
        <TabsContent value="thr" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex justify-between items-center">
                <div className="w-32">
                  <Label>Tahun</Label>
                  <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setShowThrDialog(true)} data-testid="btn-calculate-thr">
                  <Calculator className="w-4 h-4 mr-2" />Hitung THR/Bonus
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Daftar THR & Bonus {selectedYear}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : thrList.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Gift className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada THR/Bonus untuk tahun ini</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>NIK</TableHead>
                      <TableHead>Nama</TableHead>
                      <TableHead>Tipe</TableHead>
                      <TableHead>Masa Kerja</TableHead>
                      <TableHead className="text-right">Jumlah</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {thrList.map((item) => (
                      <TableRow key={item.id} data-testid={`thr-row-${item.id}`}>
                        <TableCell className="font-mono">{item.employee_nik}</TableCell>
                        <TableCell className="font-medium">{item.employee_nama}</TableCell>
                        <TableCell><Badge variant="outline">{item.tipe.toUpperCase()}</Badge></TableCell>
                        <TableCell>{item.masa_kerja_bulan} bulan</TableCell>
                        <TableCell className="text-right font-bold">{formatRupiah(item.jumlah)}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Payroll Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Payroll</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bulan</Label>
                <Select value={String(generateMonth)} onValueChange={(v) => setGenerateMonth(parseInt(v))}>
                  <SelectTrigger data-testid="select-gen-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {new Date(2025, i).toLocaleDateString('id-ID', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tahun</Label>
                <Select value={String(generateYear)} onValueChange={(v) => setGenerateYear(parseInt(v))}>
                  <SelectTrigger data-testid="select-gen-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-sm text-slate-500">
              Payroll akan digenerate untuk semua karyawan aktif yang sudah memiliki struktur gaji.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Batal</Button>
            <Button onClick={handleGeneratePayroll} disabled={loading} data-testid="btn-confirm-generate">
              {loading ? "Processing..." : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Salary Structure Dialog */}
      <Dialog open={showSalaryDialog} onOpenChange={setShowSalaryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Tambah/Edit Struktur Gaji</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Karyawan</Label>
              <Select value={salaryForm.employee_id} onValueChange={(v) => setSalaryForm({...salaryForm, employee_id: v})}>
                <SelectTrigger data-testid="select-employee">
                  <SelectValue placeholder="Pilih karyawan" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'aktif').map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.nik} - {e.nama_lengkap}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gaji Pokok *</Label>
              <Input type="number" value={salaryForm.gaji_pokok} onChange={(e) => setSalaryForm({...salaryForm, gaji_pokok: e.target.value})} placeholder="5000000" data-testid="input-gaji-pokok" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tunjangan Tetap</Label>
                <Input type="number" value={salaryForm.tunjangan_tetap} onChange={(e) => setSalaryForm({...salaryForm, tunjangan_tetap: e.target.value})} placeholder="500000" />
              </div>
              <div>
                <Label>Tunjangan Makan</Label>
                <Input type="number" value={salaryForm.tunjangan_makan} onChange={(e) => setSalaryForm({...salaryForm, tunjangan_makan: e.target.value})} placeholder="500000" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tunjangan Kesehatan</Label>
                <Input type="number" value={salaryForm.tunjangan_kesehatan} onChange={(e) => setSalaryForm({...salaryForm, tunjangan_kesehatan: e.target.value})} placeholder="300000" />
              </div>
              <div>
                <Label>Tunjangan Lainnya</Label>
                <Input type="number" value={salaryForm.tunjangan_lainnya} onChange={(e) => setSalaryForm({...salaryForm, tunjangan_lainnya: e.target.value})} placeholder="0" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSalaryDialog(false)}>Batal</Button>
            <Button onClick={handleSaveSalary} disabled={loading} data-testid="btn-save-salary">
              {loading ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* THR Dialog */}
      <Dialog open={showThrDialog} onOpenChange={setShowThrDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hitung THR / Bonus</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipe</Label>
              <Select value={thrForm.tipe} onValueChange={(v) => setThrForm({...thrForm, tipe: v})}>
                <SelectTrigger data-testid="select-thr-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="thr">THR (Tunjangan Hari Raya)</SelectItem>
                  <SelectItem value="bonus">Bonus Tahunan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tahun</Label>
              <Select value={String(thrForm.tahun)} onValueChange={(v) => setThrForm({...thrForm, tahun: parseInt(v)})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jumlah Bulan Gaji</Label>
              <Input type="number" value={thrForm.jumlah_bulan} onChange={(e) => setThrForm({...thrForm, jumlah_bulan: parseFloat(e.target.value) || 1})} placeholder="1" />
              <p className="text-xs text-slate-500 mt-1">THR biasanya 1 bulan gaji</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowThrDialog(false)}>Batal</Button>
            <Button onClick={handleCalculateThr} disabled={loading} data-testid="btn-confirm-thr">
              {loading ? "Menghitung..." : "Hitung"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip Detail Dialog */}
      <Dialog open={showPayslipDialog} onOpenChange={setShowPayslipDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Slip Gaji - {selectedPayslip?.employee_nama}
            </DialogTitle>
          </DialogHeader>
          {selectedPayslip && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">NIK</p>
                  <p className="font-medium">{selectedPayslip.employee_nik}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Periode</p>
                  <p className="font-medium">{selectedPayslip.periode}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Departemen</p>
                  <p className="font-medium">{selectedPayslip.department_nama}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Posisi</p>
                  <p className="font-medium">{selectedPayslip.position_nama}</p>
                </div>
              </div>

              {/* Pendapatan */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-green-700 mb-3">Pendapatan</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span>Gaji Pokok</span><span>{formatRupiah(selectedPayslip.gaji_pokok)}</span></div>
                  <div className="flex justify-between"><span>Tunjangan Tetap</span><span>{formatRupiah(selectedPayslip.tunjangan_tetap)}</span></div>
                  <div className="flex justify-between"><span>Tunjangan Makan</span><span>{formatRupiah(selectedPayslip.tunjangan_makan)}</span></div>
                  <div className="flex justify-between"><span>Tunjangan Kesehatan</span><span>{formatRupiah(selectedPayslip.tunjangan_kesehatan)}</span></div>
                  <div className="flex justify-between"><span>Tunjangan Lainnya</span><span>{formatRupiah(selectedPayslip.tunjangan_lainnya)}</span></div>
                  <div className="flex justify-between"><span>Lembur ({selectedPayslip.lembur_jam} jam)</span><span>{formatRupiah(selectedPayslip.lembur_nominal)}</span></div>
                  <div className="flex justify-between font-bold text-green-700 border-t pt-2">
                    <span>Total Pendapatan</span>
                    <span>{formatRupiah(selectedPayslip.total_pendapatan)}</span>
                  </div>
                </div>
              </div>

              {/* Potongan */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-red-700 mb-3">Potongan</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span>Alpha ({selectedPayslip.alpha_hari} hari)</span><span>{formatRupiah(selectedPayslip.potongan_alpha)}</span></div>
                  <div className="flex justify-between"><span>Terlambat ({selectedPayslip.terlambat_kali}x)</span><span>{formatRupiah(selectedPayslip.potongan_terlambat)}</span></div>
                  <div className="flex justify-between"><span>BPJS Kesehatan</span><span>{formatRupiah(selectedPayslip.bpjs_kesehatan)}</span></div>
                  <div className="flex justify-between"><span>BPJS Ketenagakerjaan</span><span>{formatRupiah(selectedPayslip.bpjs_ketenagakerjaan)}</span></div>
                  <div className="flex justify-between"><span>PPh 21</span><span>{formatRupiah(selectedPayslip.pph21)}</span></div>
                  <div className="flex justify-between font-bold text-red-700 border-t pt-2">
                    <span>Total Potongan</span>
                    <span>{formatRupiah(selectedPayslip.total_potongan)}</span>
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between text-lg font-bold">
                  <span>Gaji Bersih (Take Home Pay)</span>
                  <span className="text-blue-700">{formatRupiah(selectedPayslip.total_gaji_bersih)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayslipDialog(false)}>Tutup</Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollPage;
